import { forwardRef, useState } from 'react';

import { ButtonProps } from './button.types';
import useComputedStyles from "@/theme/useComputedStyles";
import {buttonStyles, outLinedButtonStyles} from "@/components/ui/buttons/buttons.styles";
import {Link} from "@remix-run/react";

// Forward ref to allow direct access to the button element
const BaseButton = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			label = 'Click',
			size = 'base',
			theme = 'solid',
			variant = 'primary',
			className,
			asLink = false,
			to,
			icon,
			linkReplace,
			...props
		},
		ref
	) => {
		// Compute classes based on props
		const [pressed, setPressed] = useState<boolean>(false);

		const computedClassNames = useComputedStyles(
			[
				theme === 'solid' ? buttonStyles : outLinedButtonStyles ?? {},
				{ default: className ?? '' }
			],
			{
				disabled: props.disabled ?? false
			},
			[variant ?? '', size ?? '']
		);

		const togglePressed = () => {
			setPressed((prev) => !prev);
		};

		// Render as a button
		return (
			<button
				ref={ref}
				aria-disabled={props.disabled}
				aria-pressed={pressed}
				className={computedClassNames}
				{...props}
				tabIndex={0}
				onMouseUp={togglePressed}
				onMouseDown={togglePressed}>
				{asLink && to && (
					<Link replace={linkReplace} to={to}>
						<>
							{icon && icon}
							{label}
						</>
					</Link>
				)}
				{!asLink && (
					<>
						{props.children && props.children}
						{!props.children && (
							<>
								{icon}
								{label}
							</>
						)}
					</>
				)}
			</button>
		);
	}
);

BaseButton.displayName = 'BaseButton';
export default BaseButton;
